import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Calendar,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Event type options
const EVENT_TYPES = [
  { value: 'class', label: 'Regular Class' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'special', label: 'Special Event' },
];

// ============================================
// Attendance Form (Single Student)
// ============================================
const AttendanceForm = ({ student, programs, record, onSave, onCancel, defaultDate }) => {
  const [formData, setFormData] = useState({
    date: record?.date || defaultDate || new Date().toISOString().split('T')[0],
    program_id: record?.program_id || (student?.program_ids?.[0] || ''),
    duration_hours: record?.duration_minutes ? (record.duration_minutes / 60) : 1,
    event_type: record?.event_type || 'class',
    event_name: record?.event_name || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.program_id) {
      alert('Please select a program');
      return;
    }

    setSaving(true);
    try {
      const url = record?.id
        ? `${API_BASE}/api/admin/attendance/${record.id}`
        : `${API_BASE}/api/admin/attendance`;

      const payload = record?.id
        ? formData
        : { ...formData, student_id: student.id };

      const response = await fetch(url, {
        method: record?.id ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...payload,
          duration_minutes: Math.round((parseFloat(formData.duration_hours) || 1) * 60),
        })
      });

      if (response.ok) {
        const savedRecord = await response.json();
        onSave(savedRecord);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save attendance');
      }
    } catch (error) {
      console.error('Failed to save attendance:', error);
      alert('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  // Filter programs to student's enrolled programs
  const availablePrograms = student?.program_ids?.length > 0
    ? programs.filter(p => student.program_ids.includes(p.id))
    : programs;

  return (
    <Card className="border-2 border-green-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          {record?.id ? 'Edit Attendance' : 'Mark Attendance'}
        </CardTitle>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Recording for: <strong>{student.name}</strong>
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date */}
            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
            </div>

            {/* Program */}
            <div>
              <Label htmlFor="program_id">Program</Label>
              <select
                id="program_id"
                value={formData.program_id}
                onChange={(e) => setFormData({ ...formData, program_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select Program...</option>
                {availablePrograms.map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>

            {/* Duration */}
            <div>
              <Label htmlFor="duration">Duration (hours)</Label>
              <Input
                id="duration"
                type="number"
                min="0.25"
                step="0.25"
                value={formData.duration_hours}
                onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                className="mt-1"
                placeholder="e.g., 1.5"
              />
            </div>

            {/* Event Type */}
            <div>
              <Label htmlFor="event_type">Event Type</Label>
              <select
                id="event_type"
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {EVENT_TYPES.map(et => (
                  <option key={et.value} value={et.value}>{et.label}</option>
                ))}
              </select>
            </div>

            {/* Event Name (for special events) */}
            {formData.event_type !== 'class' && (
              <div className="md:col-span-2">
                <Label htmlFor="event_name">Event Name</Label>
                <Input
                  id="event_name"
                  value={formData.event_name}
                  onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                  placeholder="e.g., Spring Tournament 2024"
                  className="mt-1"
                />
              </div>
            )}

            {/* Notes */}
            <div className="md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any notes..."
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex space-x-2 mt-4">
            <Button type="submit" disabled={saving} variant="adminPrimary">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : (record?.id ? 'Update' : 'Mark Present')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ============================================
// Bulk Attendance Form
// ============================================
const BulkAttendanceForm = ({ students, programs, onSave, onCancel, defaultDate }) => {
  const [formData, setFormData] = useState({
    date: defaultDate || new Date().toISOString().split('T')[0],
    program_id: '',
    duration_hours: 1,
    event_type: 'class',
    event_name: '',
  });
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Filter students by selected program
  const studentsInProgram = formData.program_id
    ? students.filter(s => s.program_ids?.includes(formData.program_id))
    : students;

  const toggleStudent = (studentId) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAll = () => {
    setSelectedStudentIds(studentsInProgram.map(s => s.id));
  };

  const deselectAll = () => {
    setSelectedStudentIds([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.program_id) {
      alert('Please select a program');
      return;
    }
    if (selectedStudentIds.length === 0) {
      alert('Please select at least one student');
      return;
    }

    setSaving(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/attendance/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          student_ids: selectedStudentIds,
          date: formData.date,
          program_id: formData.program_id,
          duration_minutes: Math.round((parseFloat(formData.duration_hours) || 1) * 60),
          event_type: formData.event_type,
          event_name: formData.event_name || null,
        })
      });

      if (response.ok) {
        const savedRecords = await response.json();
        onSave(savedRecords);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save attendance');
      }
    } catch (error) {
      console.error('Failed to save bulk attendance:', error);
      alert('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-2 border-green-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5 text-green-600" />
          Bulk Attendance
        </CardTitle>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Mark attendance for multiple students at once
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Date */}
            <div>
              <Label htmlFor="bulk_date">Date</Label>
              <Input
                id="bulk_date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
            </div>

            {/* Program */}
            <div>
              <Label htmlFor="bulk_program">Program</Label>
              <select
                id="bulk_program"
                value={formData.program_id}
                onChange={(e) => {
                  setFormData({ ...formData, program_id: e.target.value });
                  setSelectedStudentIds([]);
                }}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select Program...</option>
                {programs.map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>

            {/* Duration */}
            <div>
              <Label htmlFor="bulk_duration">Duration (hours)</Label>
              <Input
                id="bulk_duration"
                type="number"
                min="0.25"
                step="0.25"
                value={formData.duration_hours}
                onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                className="mt-1"
                placeholder="e.g., 1.5"
              />
            </div>
          </div>

          {/* Student Selection */}
          {formData.program_id && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Select Students ({selectedStudentIds.length} selected)</Label>
                <div className="space-x-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={deselectAll}>
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded bg-gray-50 dark:bg-gray-800">
                {studentsInProgram.length === 0 ? (
                  <p className="col-span-full text-center text-gray-500 py-4">
                    No students enrolled in this program
                  </p>
                ) : (
                  studentsInProgram.map(student => (
                    <label
                      key={student.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        selectedStudentIds.includes(student.id)
                          ? 'bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-600'
                          : 'bg-background border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStudentIds.includes(student.id)}
                        onChange={() => toggleStudent(student.id)}
                        className="rounded"
                      />
                      <span className="text-sm truncate">{student.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <Button
              type="submit"
              disabled={saving || selectedStudentIds.length === 0}
              variant="adminPrimary"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : `Mark ${selectedStudentIds.length} Present`}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ============================================
// Attendance Record Item
// ============================================
const AttendanceRecordItem = ({ record, onEdit, onDelete, onToggleStatus }) => {
  const eventTypeLabel = EVENT_TYPES.find(et => et.value === record.event_type)?.label || record.event_type;
  const isAttended = record.status !== 'missed';
  const isAutoGenerated = record.marked_by === 'auto-generated';

  return (
    <div className={`flex items-center gap-4 p-3 rounded border hover:shadow-sm transition-shadow ${
      isAttended ? 'bg-background' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    }`}>
      {/* Status Toggle Button */}
      <button
        onClick={() => onToggleStatus(record)}
        className={`p-1.5 rounded-full transition-colors ${
          isAttended
            ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/70'
            : 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/70'
        }`}
        title={isAttended ? 'Click to mark as missed' : 'Click to mark as attended'}
      >
        {isAttended ? (
          <CheckCircle className="h-5 w-5" />
        ) : (
          <XCircle className="h-5 w-5" />
        )}
      </button>

      <div className="flex-1">
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-3 w-3 text-gray-400" />
          <span className="font-medium">{new Date(record.date).toLocaleDateString()}</span>
          <span className="text-gray-300 dark:text-gray-600 dark:text-gray-400">|</span>
          <span className="text-gray-600 dark:text-gray-400">{record.program_name}</span>
          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">{eventTypeLabel}</span>
          {!isAttended && (
            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded text-xs font-medium">
              Missed
            </span>
          )}
          {isAutoGenerated && (
            <span className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 rounded text-xs">
              Auto
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
          <Clock className="h-3 w-3" />
          <span>{(record.duration_minutes / 60).toFixed(1)} hrs</span>
          {record.event_name && (
            <>
              <span className="text-gray-300 dark:text-gray-600 dark:text-gray-400">|</span>
              <span>{record.event_name}</span>
            </>
          )}
          {record.notes && (
            <>
              <span className="text-gray-300 dark:text-gray-600 dark:text-gray-400">|</span>
              <span className="italic">{record.notes}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => onEdit(record)}>
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(record)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// Main Attendance Manager Component
// ============================================
const AttendanceManager = ({ student, students, programs }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMonth, setViewMonth] = useState(new Date());

  useEffect(() => {
    fetchRecords();
  }, [student?.id, viewMonth]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const year = viewMonth.getFullYear();
      const month = viewMonth.getMonth() + 1;

      let url = `${API_BASE}/api/admin/attendance?`;
      if (student?.id) {
        url += `student_id=${student.id}&`;
      }
      // Add date range filter based on month
      const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month, 0).toISOString().split('T')[0];
      url += `start_date=${startDate}&end_date=${endDate}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRecords(data);
      }
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await fetchRecords();
    setShowForm(false);
    setShowBulkForm(false);
    setEditingRecord(null);
  };

  const handleDelete = async (record) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) return;

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/attendance/${record.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        await fetchRecords();
      }
    } catch (error) {
      console.error('Failed to delete attendance:', error);
    }
  };

  const handleToggleStatus = async (record) => {
    const newStatus = record.status === 'missed' ? 'attended' : 'missed';
    let notes = record.notes;

    // Prompt for note when marking as missed
    if (newStatus === 'missed') {
      const note = window.prompt('Why did the student miss this class? (optional)', record.notes || '');
      if (note !== null) {
        notes = note || record.notes;
      } else {
        return; // User cancelled
      }
    }

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/attendance/${record.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus, notes })
      });
      if (response.ok) {
        await fetchRecords();
      }
    } catch (error) {
      console.error('Failed to toggle attendance status:', error);
    }
  };

  const prevMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1));
  };

  // Calculate stats
  const totalClasses = records.length;
  const totalMinutes = records.reduce((sum, r) => sum + (r.duration_minutes || 60), 0);
  const totalHours = (totalMinutes / 60).toFixed(1);

  return (
    <div className="space-y-4">
      {/* Month Navigation & Stats */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-lg">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={prevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-medium text-green-900 dark:text-green-100 min-w-[140px] text-center">
            {viewMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </span>
          <Button variant="outline" size="sm" onClick={nextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-6 text-sm">
          <div>
            <span className="text-green-700 dark:text-green-300">Classes:</span>{' '}
            <strong className="text-green-900 dark:text-green-100">{totalClasses}</strong>
          </div>
          <div>
            <span className="text-green-700 dark:text-green-300">Hours:</span>{' '}
            <strong className="text-green-900 dark:text-green-100">{totalHours}</strong>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {!showForm && !showBulkForm && (
        <div className="flex gap-2">
          {student && (
            <Button onClick={() => setShowForm(true)} variant="adminPrimary">
              <Plus className="h-4 w-4 mr-2" />
              Mark Attendance
            </Button>
          )}
          {!student && students && students.length > 0 && (
            <Button onClick={() => setShowBulkForm(true)} variant="outline">
              <Users className="h-4 w-4 mr-2" />
              Bulk Attendance
            </Button>
          )}
        </div>
      )}

      {/* Single Attendance Form */}
      {showForm && student && (
        <AttendanceForm
          key={`${viewMonth.getTime()}-${editingRecord?.id || 'new'}`}
          student={student}
          programs={programs}
          record={editingRecord}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingRecord(null); }}
          defaultDate={new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).toISOString().split('T')[0]}
        />
      )}

      {/* Bulk Attendance Form */}
      {!student && showBulkForm && students && (
        <BulkAttendanceForm
          key={viewMonth.getTime()}
          students={students}
          programs={programs}
          onSave={handleSave}
          onCancel={() => setShowBulkForm(false)}
          defaultDate={new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).toISOString().split('T')[0]}
        />
      )}

      {/* Attendance Records */}
      {!showForm && !showBulkForm && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Attendance Records ({records.length})
          </h4>
          {loading ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading...</div>
          ) : records.length === 0 ? (
            <div className="text-center py-6 text-gray-500 bg-gray-50 dark:bg-gray-800 rounded">
              No attendance records for this month.
            </div>
          ) : (
            <div className="space-y-2">
              {records.map(record => (
                <AttendanceRecordItem
                  key={record.id}
                  record={record}
                  onEdit={(r) => { setEditingRecord(r); setShowForm(true); }}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceManager;
